import { 
  useInfiniteQuery, 
  type InfiniteData, 
  type UseInfiniteQueryOptions,
  type QueryKey
} from "@tanstack/react-query";

/**
 * Standard Paginated Response Structure from SEMO Backend
 * Matches the inner 'data' field in our BaseResponse wrapper
 */
export interface SemoPaginatedResponse<T> {
  items: T[];
  meta: {
    totalCount: number;
    currentPage: number;
    totalPages: number;
    pageSize: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

/**
 * Common API Response wrapper from SEMO Backend
 */
export interface SemoApiResponse<T> {
  success: boolean;
  data: SemoPaginatedResponse<T>;
  message: string;
  statusCode: number;
}

/**
 * useSemoInfiniteQuery - Industry standard generic hook for infinite scrolling.
 * Designed to work with Orval-generated API functions.
 * 
 * @template T The type of individual items in the list
 * @template TParams The type of parameters accepted by the API function
 */
export function useSemoInfiniteQuery<T, TParams>(
  queryKey: QueryKey,
  apiFn: (params: TParams & { page: number }, signal?: AbortSignal) => Promise<unknown>,
  params: TParams,
  options?: Partial<UseInfiniteQueryOptions<SemoPaginatedResponse<T>, Error, InfiniteData<SemoPaginatedResponse<T>>, QueryKey, number>>
) {
  return useInfiniteQuery<
    SemoPaginatedResponse<T>,
    Error,
    InfiniteData<SemoPaginatedResponse<T>>,
    QueryKey,
    number
  >({
    queryKey: [...queryKey, "infinite"],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1, signal }) => {
      const response = await apiFn({ ...params, page: pageParam } as TParams & { page: number }, signal);
      
      // 1. Unwrap Axios response if present (e.g. in some test environments or if direct axios is used)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let payload = (response as any)?.data || response;
      
      // 2. Handle 'array of paginated objects' spec anomaly (sometimes OpenAPI defines a list as an array of the wrapper)
      if (Array.isArray(payload)) payload = payload[0];

      // 3. Extract the actual paginated data from the SEMO wrapper if it exists
      // The wrapper has 'success', 'data' (the payload), and 'message'
      if (payload && typeof payload === 'object' && 'data' in payload && payload.data?.items) {
        payload = payload.data;
      }

      // 4. Final validation: must have items array and meta object
      if (!payload || !Array.isArray(payload.items)) {
        console.error("[useSemoInfiniteQuery] Invalid response structure:", response);
        throw new Error("올바르지 않은 데이터 응답 구조입니다.");
      }

      return payload as SemoPaginatedResponse<T>;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.meta) return undefined;
      const { currentPage, totalPages } = lastPage.meta;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    ...options,
  });
}
